import { filing } from './toolkit';

/**
 * Official Form 106D, Schedule D: Creditors Who Have Claims Secured by Property.
 *
 * Part 1 rows 2.1 to 2.5 are the estate's open liabilities in the claim category
 * "mortgage lien" (the app's secured-claim category), in the order they were entered.
 * Creditor name and address come from the creditor catalogue (`claim`), the amounts,
 * collateral and dates from the liability itself (`liability`). Both lists hold the
 * open liabilities in id order, so row i of one is row i of the other.
 */
const LIABILITY = (i: number) => `[claimCategory=mortgage_lien][status=open][${i}]`;
const CLAIM = (i: number) => `[category=mortgage_lien][${i}]`;

const BY_HAND = 'Another answer (tick the box by hand after printing)';

interface Row {
  n: string; name: string; street: string; line2: string; city: string; state: string; zip: string;
  colA: string; colB: string; colC: string; collateral: string;
  contingent: string; unliquidated: string; disputed: string;
  agreement: string; statutory: string; judgment: string; other: string; otherText: string;
  community: string; date: string; account: string;
  /** One CheckBox field holding the four "Who owes the debt?" widgets, or four separate fields. */
  whoOwes: { group: string } | { groupSharedState: string } | { separate: [string, string, string, string] };
}

const ROWS: Row[] = [
  {
    n: '2.1', name: 'Creditors Name', street: 'Street', line2: '1', city: '2', state: '3', zip: '4',
    colA: 'undefined', colB: 'undefined_2', colC: 'undefined_3', collateral: 'As of the date you file the claim is Check all that apply',
    contingent: 'Contingent', unliquidated: 'Unliquidated', disputed: 'Disputed',
    agreement: 'An agreement you made such as mortgage or secured', statutory: 'Statutory lien such as tax lien mechanics lien',
    judgment: 'Judgment lien from a lawsuit', other: 'Other including a right to offset', otherText: 'undefined_4',
    community: 'Check if this claim relates to a', date: 'Date debt was incurred', account: 'account number',
    whoOwes: { group: 'check 2' },
  },
  ...[
    { n: '2.2', s: '_2', colA: 'undefined_5', colB: 'undefined_6', colC: 'undefined_7', otherText: 'undefined_8', account: 'account number 2', whoOwes: { group: 'check 3' } },
    { n: '2.3', s: '_3', colA: 'undefined_10', colB: 'undefined_13', colC: 'undefined_15', otherText: 'undefined_11', account: 'account number 3', whoOwes: { group: 'check 4' } },
    { n: '2.4', s: '_4', colA: 'undefined_14', colB: 'undefined_17', colC: 'undefined_18', otherText: 'undefined_16', account: 'account number 4', whoOwes: { groupSharedState: 'check 5' } },
    {
      n: '2.5', s: '_5', colA: 'undefined_20', colB: 'undefined_22', colC: 'undefined_25', otherText: 'undefined_21', account: 'account number 5',
      whoOwes: { separate: ['Debtor 1 only_5', 'Debtor 2 only_5', 'Debtor 1 and Debtor 2 only_5', 'At least one of the debtors and another_5'] as [string, string, string, string] },
    },
  ].map(r => ({
    n: r.n, name: `Creditors Name${r.s}`, street: `Street${r.s}`, line2: `1${r.s}`, city: `2${r.s}`, state: `3${r.s}`, zip: `4${r.s}`,
    colA: r.colA, colB: r.colB, colC: r.colC, collateral: `As of the date you file the claim is Check all that apply${r.s}`,
    contingent: `Contingent${r.s}`, unliquidated: `Unliquidated${r.s}`, disputed: `Disputed${r.s}`,
    agreement: `An agreement you made such as mortgage or secured${r.s}`, statutory: `Statutory lien such as tax lien mechanics lien${r.s}`,
    judgment: `Judgment lien from a lawsuit${r.s}`, other: `Other including a right to offset${r.s}`, otherText: r.otherText,
    community: `Check if this claim relates to a${r.s}`, date: `Date debt was incurred${r.s}`, account: r.account,
    whoOwes: r.whoOwes,
  } as Row)),
];

/** Part 2 (others to be notified), six blocks. Row 4's account box is the PDF field "account number", shared with 2.1. */
const PART2 = [
  { box: 'be notified for any debts in Part 1 do not fill out or submit this page.0', name: 'Name', line: 'On which line in Part 1 did you enter the creditor', account: 'account number 6', street: 'Number_6', line2: 'Street 1', city: 'Street 2', state: 'Street 3', zip: 'Street 4' },
  { box: 'be notified for any debts in Part 1 do not fill out or submit this page.1', name: 'Name_2', line: 'undefined_26', account: 'account number 7', street: 'Number_7', line2: 'Street 1_2', city: 'Street 2_2', state: 'Street 3_2', zip: 'Street 4_2' },
  { box: 'be notified for any debts in Part 1 do not fill out or submit this page.2', name: 'Name_3', line: 'undefined_27', account: 'account number 8', street: 'Number_8', line2: 'Street 1_3', city: 'Street 2_3', state: 'Street 3_3', zip: 'Street 4_3' },
  { box: 'be notified for any debts in Part 1 do not fill out or submit this page.3', name: 'Name_4', line: 'On which line in Part 1 did you enter the creditor_4', account: null, street: 'Number_9', line2: 'Street 1_4', city: 'Street 2_4', state: 'Street 3_4', zip: 'Street 4_4' },
  { box: 'be notified for any debts in Part 1 do not fill out or submit this page.4.0', name: 'Name_5', line: 'On which line in Part 1 did you enter the creditor_5', account: 'account number 10', street: 'Number_10', line2: 'Street 1_5', city: 'Street 2_5', state: 'Street 3_5', zip: 'Street 4_5' },
  { box: 'be notified for any debts in Part 1 do not fill out or submit this page.4.1', name: 'Name_6', line: 'On which line in Part 1 did you enter the creditor_6', account: 'account number 11', street: 'Number_11', line2: 'Street 1_6', city: 'Street 2_6', state: 'Street 3_6', zip: 'Street 4_6' },
];

const PART2_REASON = 'Part 2 is used only when someone besides the Part 1 creditor (for example a collection agency or a co-creditor) must be notified. '
  + 'The app records one creditor per liability and has no record of other notice parties, so this page is not completed by the software.';

export default filing('file-1787253699908-d61b7e4c-4020-4dce-b060-274c1a33fb55.pdf', 'Schedule D: creditors with secured claims', f => {
  // Caption (the same Debtor 1 and Case number fields print at the top of pages 2 and 3).
  f.shared('Debtor 1', 'decedent.fullName', 'Debtor 1 (the decedent): first, middle and last name (also printed on pages 2 and 3)');
  f.notApplicable('Debtor 2', 'Debtor 2 (spouse, if filing jointly)',
    "A joint filing is made only by two living spouses. The decedent's estate is the only debtor on this schedule.");
  f.notApplicable('Bankruptcy District Information', 'United States Bankruptcy Court for the: district',
    "The schedule is used here as a list of the decedent's secured creditors in the estate proceeding, not in a bankruptcy case, so no bankruptcy district applies.");
  f.shared('Case number', 'estate.courtFileNumber', "Case number (the Surrogate's Court file number; also printed on pages 2 and 3)");
  f.choice('amendedFiling', 'Check if this is an amended filing', 'Filing', [
    { label: 'Yes, this amends a schedule already filed', value: 'Yes', fields: ['Check if this is an'] },
    { label: 'No, this is the first filing', value: 'No', fields: [] },
  ], 'checkbox');

  f.notApplicable('check1', '1. Do any creditors have claims secured by your property? (No / Yes)',
    'The No and Yes boxes are two widgets of one PDF checkbox field with different on-states; the PDF writer can only tick the first (No). '
    + 'When secured claims are listed, tick Yes by hand.');

  // Part 1: secured claims.
  ROWS.forEach((r, i) => {
    const section = `${r.n} Secured claim ${i + 1}`;
    const k = `securedClaim${i + 1}`;
    const p = `${r.n}`;
    f.shared(r.name, 'claim.name', `${p} Creditor's name`, { selector: CLAIM(i) });
    f.shared(r.street, 'claim.address', `${p} Creditor's address (number, street, city, state, ZIP, printed on one line)`, { selector: CLAIM(i) });
    const addressReason = "The creditor catalogue stores the creditor's address as one text; it prints in full on the Number/Street line.";
    f.notApplicable(r.line2, `${p} Creditor's address: second line`, addressReason);
    f.notApplicable(r.city, `${p} Creditor's address: city`, addressReason);
    f.notApplicable(r.state, `${p} Creditor's address: state`, addressReason);
    f.notApplicable(r.zip, `${p} Creditor's address: ZIP code`, addressReason);

    if ('group' in r.whoOwes) {
      f.choice(`${k}WhoOwes`, `${p} Who owes the debt?`, section, [
        { label: 'Debtor 1 only (the decedent)', value: 'debtor1', fields: [r.whoOwes.group] },
        { label: BY_HAND, value: 'other', fields: [] },
      ], 'checkbox',
      'The four boxes of this row are one PDF field, so only "Debtor 1 only" can be ticked here; tick any other answer by hand.');
    } else if ('groupSharedState' in r.whoOwes) {
      f.notApplicable(r.whoOwes.groupSharedState, `${p} Who owes the debt? (four boxes)`,
        'The four boxes of this row are one PDF checkbox field with one shared on-state: ticking any one ticks all four. Tick the answer by hand.');
    } else {
      const [d1, d2, both, another] = r.whoOwes.separate;
      f.choice(`${k}WhoOwes`, `${p} Who owes the debt?`, section, [
        { label: 'Debtor 1 only (the decedent)', value: 'debtor1', fields: [d1] },
        { label: 'Debtor 2 only', value: 'debtor2', fields: [d2] },
        { label: 'Debtor 1 and Debtor 2 only', value: 'both', fields: [both] },
        { label: 'At least one of the debtors and another', value: 'another', fields: [another] },
      ], 'checkbox', 'Usually matches "Who owes the debt?" on the liability.');
    }
    f.choice(`${k}Community`, `${p} Check if this claim relates to a community debt`, section, [
      { label: 'Yes, a community debt', value: 'Yes', fields: [r.community] },
      { label: 'No', value: 'No', fields: [] },
    ], 'checkbox');
    f.shared(r.date, 'liability.dateIncurred', `${p} Date debt was incurred`, { selector: LIABILITY(i) });

    f.shared(r.collateral, 'liability.collateralDescription', `${p} Describe the property that secures the claim`, { selector: LIABILITY(i) });
    const yesNo = (key: string, label: string, field: string) => f.choice(`${k}${key}`, `${p} ${label}`, section, [
      { label: 'Yes', value: 'Yes', fields: [field] },
      { label: 'No', value: 'No', fields: [] },
    ], 'checkbox');
    yesNo('Contingent', 'As of the date you file, the claim is contingent', r.contingent);
    yesNo('Unliquidated', 'As of the date you file, the claim is unliquidated', r.unliquidated);
    yesNo('Disputed', 'As of the date you file, the claim is disputed', r.disputed);
    yesNo('LienAgreement', 'Nature of lien: an agreement you made (such as mortgage or secured car loan)', r.agreement);
    yesNo('LienStatutory', "Nature of lien: statutory lien (such as tax lien, mechanic's lien)", r.statutory);
    yesNo('LienJudgment', 'Nature of lien: judgment lien from a lawsuit', r.judgment);
    yesNo('LienOther', 'Nature of lien: other (including a right to offset)', r.other);
    f.shared(r.otherText, 'liability.lienNature', `${p} Nature of lien, other: description (the liability's "Nature of lien" text)`, { selector: LIABILITY(i) });
    f.shared(r.account, 'liability.accountLast4', `${p} Last 4 digits of account number (from the liability account reference)`, { selector: LIABILITY(i) });

    f.shared(r.colA, 'liability.outstandingBalance', `${p} Column A: amount of claim (do not deduct the value of collateral)`, { selector: LIABILITY(i) });
    f.shared(r.colB, 'liability.collateralValue', `${p} Column B: value of collateral that supports this claim`, { selector: LIABILITY(i) });
    f.shared(r.colC, 'liability.unsecuredAmount', `${p} Column C: unsecured portion, if any (outstanding balance less secured portion)`, { selector: LIABILITY(i) });
  });

  f.notApplicable('undefined_12', 'Additional page, first entry number (2.3)',
    'The entry number is fixed by its position (2.3). The catalogue cannot print literal text, so it is written by hand when this entry is used.');
  f.notApplicable('undefined_19', 'Additional page, second and third entry numbers (2.4 and 2.5)',
    'One PDF field is printed in both entry-number boxes (2.4 and 2.5), so no single value is correct for both. Write the numbers by hand.');

  // Column A totals.
  f.shared('undefined_9', 'totals.securedClaimsFirstPageTotal', 'Page 1: total of Column A (entries 2.1 and 2.2), computed from secured liabilities');
  f.shared('undefined_23', 'totals.securedClaimsAdditionalPageTotal', 'Additional page: total of Column A (entries 2.3 to 2.5), computed');
  f.shared('undefined_24', 'totals.securedClaimsTotal', 'Last page: total of Column A from all pages, computed');

  // Page numbers.
  f.question('totalPages', 'Total number of pages of this schedule (page 1 of ...)', 'Page numbers', 'number');
  f.answer('fill_27.0', 'totalPages', 'Page 1 of: total pages');
  f.answer('fill_27.1.1', 'totalPages', 'Additional page: of (total pages)');
  f.input('fill_27.1.0', 'additionalPageNumber', 'Additional page: page number', 'Page numbers', 'number');

  // Part 2: others to be notified (page 3).
  PART2.forEach((r, i) => {
    const p = `Part 2, notice party ${i + 1}`;
    f.notApplicable(r.box, `${p}: entry number box`, PART2_REASON);
    f.notApplicable(r.name, `${p}: name`, PART2_REASON);
    f.notApplicable(r.line, `${p}: on which line in Part 1 did you enter the creditor?`, PART2_REASON);
    if (r.account) f.notApplicable(r.account, `${p}: last 4 digits of account number`, PART2_REASON);
    f.notApplicable(r.street, `${p}: number and street`, PART2_REASON);
    f.notApplicable(r.line2, `${p}: second address line`, PART2_REASON);
    f.notApplicable(r.city, `${p}: city`, PART2_REASON);
    f.notApplicable(r.state, `${p}: state`, PART2_REASON);
    f.notApplicable(r.zip, `${p}: ZIP code`, PART2_REASON);
  });
  f.notApplicable('fill_28.1.1', 'Part 2 page: page number', PART2_REASON);
  f.notApplicable('fill_28.1.2', 'Part 2 page: of (total pages)', PART2_REASON);

  const BUTTON = 'Interactive button of the fillable PDF (print, save, attach, reset); not a data field.';
  f.notApplicable('Button.Print1', 'Print button', BUTTON);
  f.notApplicable('Button.SaveAs', 'Save As button', BUTTON);
  f.notApplicable('attach', 'Add Attachment button', BUTTON);
  f.notApplicable('Button.Reset', 'Reset button', BUTTON);
});
